import type { ShortcutService } from '.';
import type { Logger } from '@thinkalpha/ui-ioc/core/types/Logger';
import { inject, injectable } from 'src/features/ioc';
import type { CommandAction } from 'src/services/CommandPaletteService/protocol';
import type { BindableCommand } from 'src/services/commands/types';
import { chordMatchesEvent } from 'src/services/commands/types';
import type { ReactBindings } from 'src/types/bindings';

const SKIP_TARGETS = new Set(['INPUT', 'TEXTAREA', 'SELECT']);

@injectable()
export class ShortcutServiceImpl implements ShortcutService {
    #controller = new AbortController();
    #log: Logger;
    #workspaceService: ReactBindings['WorkspaceService'];
    #commandPaletteService: ReactBindings['CommandPaletteService'];
    #keybindingsService: ReactBindings['KeybindingsService'];

    constructor(
        @inject('WorkspaceService') workspaceService: ReactBindings['WorkspaceService'],
        @inject('Logger') logger: ReactBindings['Logger'],
        @inject('CommandPaletteService') commandPaletteService: ReactBindings['CommandPaletteService'],
        @inject('KeybindingsService') keybindingsService: ReactBindings['KeybindingsService'],
    ) {
        this.#log = logger.getSubLogger({ name: 'ShortcutService' });
        this.#workspaceService = workspaceService;
        this.#commandPaletteService = commandPaletteService;
        this.#keybindingsService = keybindingsService;

        const { signal } = this.#controller;
        window.addEventListener('keydown', this.#handleKeyDown, { signal });
        window.addEventListener('keyup', this.#handleKeyUp, { signal });
        window.addEventListener('blur', this.#handleWindowBlur, { signal });
    }

    #handleKeyDown = (event: KeyboardEvent) => {
        if (this.#shouldSkipEvent(event)) return;

        const bindings = this.#keybindingsService.bindings.value;
        for (const command of this.#keybindingsService.commands) {
            const chord = bindings.get(command.id);
            if (!chord) continue;
            if (!chordMatchesEvent(chord, event)) continue;

            event.preventDefault();
            event.stopPropagation();

            const enabled = command.enabled ? command.enabled.value : true;
            if (enabled) {
                if (TRACE) {
                    this.#log.trace('Shortcut dispatched', { commandId: command.id });
                }
                this.#dispatch(command);
            }
            return;
        }
    };

    #dispatch(command: BindableCommand): void {
        const action = command.action;
        switch (action.eventKey) {
            case 'commandPalette::toggle':
                this.#commandPaletteService.toggle();
                return;
            case 'keybindings::openSettings':
                this.#keybindingsService.openSettings();
                return;
            default:
                this.#dispatchToWorkspace(action);
                return;
        }
    }

    #dispatchToWorkspace(action: CommandAction): void {
        this.#workspaceService.executeCommand(action);
    }

    #handleKeyUp = (event: KeyboardEvent) => {
        if (event.key === 'Control') {
            this.#workspaceService.handleCycleFocusKeyUp();
        }
    };

    #handleWindowBlur = () => {
        this.#workspaceService.handleCycleFocusKeyUp();
    };

    #shouldSkipEvent(event: KeyboardEvent): boolean {
        const target = event.target as Element | null;
        if (!target) return false;
        if (SKIP_TARGETS.has(target.tagName)) return true;
        if (target instanceof HTMLElement && (target.isContentEditable || target.contentEditable === 'true')) {
            return true;
        }
        return false;
    }

    [Symbol.dispose](): void {
        this.#controller.abort();
    }
}
